import { useState } from "react";
import { Link } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { useProjects } from "../../api/queries";
import { Button } from "../../components/Button";
import { sessionsWithDevelopLaunchHref } from "../develop/launchParams";
import { DeployWizard } from "../deploy-wizard/DeployWizard";
import { CreateProjectModal } from "../projects/CreateProjectModal";
import { AdoptEntryIcon, DevelopEntryIcon, ScaffoldEntryIcon } from "./homeIcons";
import { RecentActivity } from "./RecentActivity";
import "./home.css";

/**
 * Home (IA slice 1, docs/product/ui-information-architecture.md §3) — the
 * "what do you want to run?" landing every fleet role's `/` renders now
 * (see routes/RoleLanding.tsx), replacing the old `/get-started` first-run
 * screen AND the old "send everyone straight to /containers" behavior: this
 * is not just a first-run page, it's every visit's front door.
 *
 * Three entries, matching the RFC's "one question, three answers": describe
 * it (Develop), adopt what's running (Migrator on-ramp), or start from a
 * scaffold (Gallery). "Adopt" and "Scaffold" never mutate anything on click —
 * they navigate to the real page that owns the actual import/browse flow
 * (one source of truth per action, A-5) — so they're safe to show to every
 * fleet role, including a read-only viewer; the page each one lands on
 * enforces its own create/manage gating (S-8).
 *
 * "Develop it" would ideally launch straight into a develop session, but
 * `StartDevelopDialog` requires an existing `projectId` (a develop session
 * co-authors ONE project's corpus, never started context-free). UX polish
 * wave 2 decision 6 gets as close to that promise as the data model allows:
 * a tenant with at least one project (or a role that can't create one
 * anyway, `offerCreateProject`) still links to /projects (pick or create the
 * one you're developing, same fallback the RFC anticipates for slice 1) —
 * but an operator/admin on a tenant with NONE yet — nothing to pick — opens
 * `CreateProjectModal` right here, and a successful create lands straight on
 * the new project's Sessions tab with the develop launcher already open
 * (`sessionsWithDevelopLaunchHref` / `ProjectSessionsTab`'s one-shot pickup),
 * so "Develop it" actually delivers the develop conversation, not just a
 * project shell to develop later.
 */
export function HomePage() {
  const { session } = useAuth();
  const isAdmin = session?.role === "admin";
  // Same gate ProjectsListPage's own "New project" button uses (project:create is
  // operator/admin server-side, S-8) — a viewer gets the plain /projects link
  // regardless of whether any project exists, since opening a create dialog they
  // can't submit would be a dead-end affordance (A-10), not a shortcut.
  const canCreateProject = canManageFleet(session?.role);
  // Bounded existence check (matches ProvisionInstanceModal's precedent for "does
  // anything exist" reads) — never a second cached copy of the tenant's project count,
  // just whether the first page came back empty (A-5).
  const { data: projectsPage } = useProjects({ limit: 1 });
  // Undefined (still loading) reads as "assume yes" so the card doesn't flash into the
  // create-first affordance before the read resolves — same "assume yes while unknown"
  // shape as PlacementsSection's `hasEligibleNodes`.
  const hasProjects = projectsPage === undefined || projectsPage.items.length > 0;
  const offerCreateProject = canCreateProject && !hasProjects;
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  return (
    <div className="home-page">
      <div className="home-page__hero">
        <h1 className="home-page__title">What do you want to run?</h1>
        <p className="home-page__subtitle">
          Start from your app — Comebye handles the machines, the deploys, and the guardrails.
        </p>
      </div>

      <div className="home-page__entries">
        <article className="home-entry">
          <DevelopEntryIcon className="home-entry__icon" />
          <h2 className="home-entry__title">Develop it</h2>
          <p className="home-entry__description">
            Co-author your app's corpus with an agent — vision, services, rules — then deploy from it.
          </p>
          {offerCreateProject ? (
            <Button variant="primary" onClick={() => setCreateProjectOpen(true)}>
              Start developing
            </Button>
          ) : (
            <Link href="/projects" className="button button--primary">
              Start developing
            </Link>
          )}
        </article>

        <article className="home-entry">
          <AdoptEntryIcon className="home-entry__icon" />
          <h2 className="home-entry__title">Adopt what's running</h2>
          <p className="home-entry__description">
            Point Comebye at your existing hosts and compose stacks; they become governed applications.
          </p>
          <Link href="/import" className="button button--secondary">
            Adopt infrastructure
          </Link>
        </article>

        <article className="home-entry">
          <ScaffoldEntryIcon className="home-entry__icon" />
          <h2 className="home-entry__title">Start from a scaffold</h2>
          <p className="home-entry__description">Pick a proven stack from the gallery and deploy it.</p>
          {canCreateProject ? (
            <Button variant="secondary" onClick={() => setWizardOpen(true)}>
              Deploy a scaffold
            </Button>
          ) : (
            <Link href="/gallery" className="button button--secondary">
              Browse scaffolds
            </Link>
          )}
        </article>
      </div>

      {/* Admin-only: see RecentActivity's doc comment — audit_log:view is
       * admin-only server-side, and there is no cheaper fleet-wide activity
       * read an operator/viewer role can call, so this facet is honestly
       * omitted for them rather than faked (A-10). */}
      {isAdmin && <RecentActivity />}

      {offerCreateProject && (
        <CreateProjectModal
          open={createProjectOpen}
          onClose={() => setCreateProjectOpen(false)}
          postCreateHref={(created) => sessionsWithDevelopLaunchHref(created.id)}
        />
      )}

      {canCreateProject && (
        <DeployWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      )}
    </div>
  );
}
